import { joiResolver } from "@hookform/resolvers/joi";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import Joi from "joi";
import { useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { CaseHeroLogo } from "../../shared/components/CaseHeroLogo";
import HLCheckboxWithLabel from "../../shared/components/Form/HLCheckboxWithLabel";
import HLTextField from "../../shared/components/Form/HLTextField";
import {
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signOut,
} from "@aws-amplify/auth";
import illustration from "../../../../assets/auth-illustration.jpg";

/**
 * Field caption sitting above the input rather than floating inside it,
 * so both fields read at a glance before either is focused.
 */
const FieldLabel = ({
  htmlFor,
  children,
}: {
  htmlFor: string;
  children: React.ReactNode;
}) => (
  <Typography
    component="label"
    htmlFor={htmlFor}
    variant="overline"
    sx={{ display: "block", mb: 0.75, color: "text.secondary" }}
  >
    {children}
  </Typography>
);

const schema = Joi.object({
  emailAddress: Joi.string()
    .email({ allowUnicode: false, tlds: { allow: false } })
    .required()
    .messages({
      "string.empty": "E-Mail-Addresse erforderlich",
      "any.required": "E-Mail-Addresse erforderlich",
      "string.email": "E-Mail-Adresse ist ungültig",
    }),
  password: Joi.string().required().messages({
    "string.empty": "Passwort erforderlich",
    "any.required": "Passwort erforderlich",
  }),
}).options({ stripUnknown: true });

// Login form to verify the user via Cognito
const Login = () => {
  const [error, setError] = useState<string>();
  const navigate = useNavigate();

  const { state } = useLocation();

  const methods = useForm({
    mode: "all",
    defaultValues: {
      emailAddress: "",
      password: "",
      rememberMe: false,
    },
    resolver: joiResolver(schema),
  });
  const {
    formState: { isSubmitting },
  } = methods;

  const handleSubmit: SubmitHandler<{
    emailAddress: string;
    password: string;
    rememberMe: boolean;
  }> = async ({ emailAddress, password, rememberMe }) => {
    try {
      let user: any = null;
      let session: any = null;

      try {
        // Get current user and session in Amplify v6
        user = await getCurrentUser();
        session = await fetchAuthSession();
      } catch (e) {
        // User not authenticated, will sign in below
      }

      // If no valid session, perform sign-in
      if (!user || !session?.tokens?.idToken) {
        const res = await signIn({ username: emailAddress, password });
        if (
          res?.nextStep.signInStep ===
          "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
        ) {
          // Key referenced on next page (in case user navigates away to their email)
          localStorage.setItem("tempUser", emailAddress);
          navigate("/newpassword");
          return;
        }
        // Refresh session after sign-in
        session = await fetchAuthSession();
      }

      // Validate that we have a valid token
      if (!session?.tokens?.idToken) {
        setError("Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.");
        return;
      }

      // If user is assigned (at least) one role, navigate to overview.
      // Never log `session` - it carries the raw id/access tokens.
      // In Amplify v6, the session is accessed differently
      const groups: string[] =
        (session.tokens?.accessToken?.payload?.[
          "cognito:groups"
        ] as string[]) ?? [];
      if (groups.find((value) => value.includes("Admin"))) {
        // Store in appropriate storage based on rememberMe choice
        // - localStorage for persistent session (remember Me checked)
        // - sessionStorage for session-only (remember Me unchecked)
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem("rememberMe", "true");
        storage.setItem("user", emailAddress);

        // Clear the opposite storage to prevent conflicts
        const oppositeStorage = rememberMe ? sessionStorage : localStorage;
        oppositeStorage.removeItem("rememberMe");
        oppositeStorage.removeItem("user");

        //@ts-ignore - custom state keys are not defined in types
        navigate(state?.prevPath ?? "/");
      } else {
        navigate("/error");
      }
    } catch (err: any) {
      const translateErr = (errMsg: string) => {
        switch (errMsg) {
          case "Incorrect username or password.":
            return "E-Mail-Adresse oder Passwort ist falsch.";
          case "Password attempts exceeded.":
            return "Passwortversuche überschritten.";
          case "Cannot read property 'length' of undefined":
            return "Keine Administratorrechte.";
          default:
            return errMsg;
        }
      };
      setError(translateErr(err.message));
    }
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        display: "flex",
        backgroundColor: "background.default",
      }}
    >
      {/* Illustration. Decorative only, so it carries an empty alt and
          drops out entirely below md rather than squeezing the form. */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "relative",
          flex: "1 1 50%",
          minWidth: 0,
          backgroundColor: "background.subtle",
        }}
      >
        <Box
          component="img"
          src={illustration}
          alt=""
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      </Box>

      {/* Form */}
      <Box
        sx={{
          flex: { xs: "1 1 100%", md: "1 1 50%" },
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          px: { xs: 3, sm: 6, lg: 10 },
          py: { xs: 4, sm: 6 },
        }}
      >
        <CaseHeroLogo open />

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            maxWidth: 380,
            width: "100%",
          }}
        >
          <Typography component="h1" variant="h1" sx={{ mb: 1 }}>
            Anmeldung
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
            Melde dich mit deinem CaseHero-Konto an.
          </Typography>

          <FormProvider {...methods}>
            <Box
              component="form"
              noValidate
              onSubmit={methods.handleSubmit(handleSubmit)}
              sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
            >
              <Box>
                <FieldLabel htmlFor="emailAddress">E-Mail-Adresse</FieldLabel>
                <HLTextField
                  name="emailAddress"
                  id="emailAddress"
                  type="email"
                  autoComplete="email"
                  required
                />
              </Box>

              <Box>
                <FieldLabel htmlFor="password">Passwort</FieldLabel>
                <HLTextField
                  name="password"
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                />
              </Box>

              {error && <Alert severity="error">{error}</Alert>}

              <HLCheckboxWithLabel
                name="rememberMe"
                label="Angemeldet bleiben"
              />

              <Button
                type="submit"
                fullWidth
                size="large"
                variant="contained"
                color="secondary"
                disabled={isSubmitting}
              >
                {!isSubmitting ? "Anmelden" : <CircularProgress size="22px" />}
              </Button>
            </Box>
          </FormProvider>

          {/* Kept from the original page: these routes are not built yet,
              so the links stay commented rather than dead. */}
          {/* <Grid container>
            <Grid size={{ xs: 12 }}>
              <MUIRouterLink to='/register'>
                Konto erstellen
              </MUIRouterLink>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <MUIRouterLink to='/forgotpassword'>
                Passwort vergessen?
              </MUIRouterLink>
            </Grid>
          </Grid> */}
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
